import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RefundRequest, RefundRequestStatus } from "../orders/types";
import { approveRefund, getRefundRequest, listRefundRequests, refreshRefund, rejectRefund } from "./admin-refunds-api";

const KEY = ["admin-refund-requests"] as const;

export const useRefundRequests = (status: RefundRequestStatus | null) =>
  useInfiniteQuery({
    queryKey: [...KEY, "list", status],
    queryFn: ({ pageParam }) => listRefundRequests(status, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before_id,
  });

export const useRefundRequest = (id: string | null) =>
  useQuery({ queryKey: [...KEY, id], queryFn: () => getRefundRequest(id as string), enabled: Boolean(id) });

/** Approve, reject and re-check; each answers with the request, and the lists are re-read. */
export function useRefundReview() {
  const qc = useQueryClient();
  const settled = (request: RefundRequest) => {
    qc.setQueryData([...KEY, request.id], request);
    void qc.invalidateQueries({ queryKey: [...KEY, "list"] });
  };
  return {
    approve: useMutation({
      mutationFn: ({ id, note }: { id: string; note: string | null }) => approveRefund(id, note),
      onSuccess: settled,
    }),
    reject: useMutation({
      mutationFn: ({ id, note }: { id: string; note: string }) => rejectRefund(id, note),
      onSuccess: settled,
    }),
    refresh: useMutation({ mutationFn: (id: string) => refreshRefund(id), onSuccess: settled }),
  };
}
